import React, { useEffect, useRef, useState } from 'react';
import AdminLayout from './AdminLayout';
import { adminApi } from '../../lib/adminAuth';
import { useSiteSettings, buildWhatsAppUrl } from '../../lib/settings';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { fileToCompressedDataUrl } from '../../lib/imageUtils';
import { Loader2, Save, MessageCircle, Phone, Mail, ExternalLink, CheckCircle2, ImagePlus, X, ArrowUp, ArrowDown, Camera, Bell, BellOff, Send as SendIcon } from 'lucide-react';
import { isPushSupported, getPushConfig, subscribeToPush, unsubscribeFromPush, getCurrentSubscription, sendTestPush } from '../../lib/pushClient';

const MAX_HERO_SLIDES = 6;

const AdminSettingsPage = () => {
  const { toast } = useToast();
  const { reload } = useSiteSettings();
  const [form, setForm] = useState({ whatsapp_number: '', contact_email: '', contact_phone: '', hero_slides: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Push notification state
  const [pushSupported] = useState(isPushSupported());
  const [pushConfig, setPushConfig] = useState(null);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushPermission, setPushPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const cfg = await getPushConfig();
        if (!mounted) return;
        setPushConfig(cfg);
        const sub = await getCurrentSubscription();
        if (mounted) setPushSubscribed(!!sub);
      } catch { /* ignore */ }
    })();
    return () => { mounted = false; };
  }, []);

  const enablePush = async () => {
    if (!pushConfig?.enabled || !pushConfig?.public_key) {
      toast({ title: 'Push not configured', description: 'Server VAPID keys missing.' });
      return;
    }
    setPushBusy(true);
    try {
      await subscribeToPush(pushConfig.public_key);
      setPushSubscribed(true);
      setPushPermission('granted');
      toast({ title: 'Push notifications enabled', description: "You'll now get notified even when this tab is closed." });
    } catch (err) {
      toast({ title: 'Could not enable', description: err.message || 'Permission was denied.' });
    } finally { setPushBusy(false); }
  };

  const disablePush = async () => {
    setPushBusy(true);
    try {
      await unsubscribeFromPush();
      setPushSubscribed(false);
      toast({ title: 'Push notifications disabled' });
    } catch { /* ignore */ } finally { setPushBusy(false); }
  };

  const testPush = async () => {
    setPushBusy(true);
    try {
      await sendTestPush();
      toast({ title: 'Test sent', description: 'Check for a notification pop-up.' });
    } catch (err) {
      toast({ title: 'Test failed', description: err.response?.data?.detail || 'Try again' });
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    adminApi.get('/settings')
      .then((r) => setForm({
        whatsapp_number: r.data.whatsapp_number || '',
        contact_email: r.data.contact_email || '',
        contact_phone: r.data.contact_phone || '',
        hero_slides: Array.isArray(r.data.hero_slides) ? r.data.hero_slides : [],
      }))
      .catch(() => toast({ title: 'Failed to load settings' }))
      .finally(() => setLoading(false));
  }, [toast]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleAddSlides = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const remaining = MAX_HERO_SLIDES - form.hero_slides.length;
    if (remaining <= 0) {
      toast({ title: 'Slide limit reached', description: `Maximum ${MAX_HERO_SLIDES} slides allowed.` });
      return;
    }
    const accepted = files.slice(0, remaining);
    setUploading(true);
    const results = [];
    for (const f of accepted) {
      if (!f.type.startsWith('image/')) {
        toast({ title: 'Invalid file', description: `${f.name} is not an image.` });
        continue;
      }
      if (f.size > 10 * 1024 * 1024) {
        toast({ title: 'File too large', description: `${f.name} exceeds 10MB.` });
        continue;
      }
      try {
        // Hero images can be wider; use 1920 max dim
        const dataUrl = await fileToCompressedDataUrl(f, { maxDim: 1920, quality: 0.85 });
        results.push(dataUrl);
      } catch (err) {
        toast({ title: 'Upload failed', description: err.message || 'Could not process image.' });
      }
    }
    if (results.length) {
      setForm((f) => ({ ...f, hero_slides: [...f.hero_slides, ...results].slice(0, MAX_HERO_SLIDES) }));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeSlide = (idx) => setForm((f) => ({ ...f, hero_slides: f.hero_slides.filter((_, i) => i !== idx) }));

  const moveSlide = (idx, dir) => {
    setForm((f) => {
      const next = [...f.hero_slides];
      const target = idx + dir;
      if (target < 0 || target >= next.length) return f;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, hero_slides: next };
    });
  };

  const resetSlides = () => {
    if (window.confirm('Reset to default slides? This will remove your custom hero images.')) {
      setForm((f) => ({ ...f, hero_slides: [] }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const wa = form.whatsapp_number.trim();
    if (!wa) { toast({ title: 'WhatsApp number required' }); return; }
    const digits = wa.replace(/\D/g, '');
    if (digits.length < 10) { toast({ title: 'Invalid number', description: 'Enter a valid phone number with country code.' }); return; }
    setSaving(true);
    try {
      await adminApi.patch('/settings', form);
      toast({ title: 'Settings saved', description: 'Changes are now live on the site.' });
      reload();
    } catch (err) {
      toast({ title: 'Save failed', description: err.response?.data?.detail || 'Try again' });
    } finally { setSaving(false); }
  };

  if (loading) {
    return <AdminLayout title="Settings"><div className="py-24 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#9a1b2f]" /></div></AdminLayout>;
  }

  const previewUrl = buildWhatsAppUrl(form.whatsapp_number, 'Hi, I am interested in a property listed on SafeHouse');

  return (
    <AdminLayout title="Settings">
      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-1 text-sm uppercase tracking-wider flex items-center gap-2"><MessageCircle className="w-4 h-4 text-emerald-500" /> WhatsApp Number</h3>
            <p className="text-xs text-gray-500 mb-4">This number receives every WhatsApp click from the property cards & detail pages. Include country code (e.g. <span className="font-mono">+91</span>).</p>
            <Label htmlFor="wa" className="text-xs">WhatsApp Number *</Label>
            <Input
              id="wa"
              value={form.whatsapp_number}
              onChange={(e) => set('whatsapp_number', e.target.value)}
              placeholder="+91 98100 00000"
              className="h-11 mt-1 text-base font-mono"
            />
            <div className="mt-3 flex items-center gap-2 text-[11px] text-gray-500">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
              Only digits are used to build the WhatsApp link. Spaces & symbols are ignored.
            </div>
          </div>

          {/* Browser Push Notifications */}
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider flex items-center gap-2"><Bell className="w-4 h-4 text-[#9a1b2f]" /> Browser Push Notifications</h3>
              <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${pushSubscribed ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                {pushSubscribed ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mb-4">Get OS-level notifications for new service requests, property submissions, and interests — even when the admin tab is closed.</p>

            {!pushSupported ? (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
                Your browser doesn't support Web Push. Try Chrome, Edge, Firefox, or Safari 16.4+.
              </div>
            ) : pushConfig && !pushConfig.enabled ? (
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
                Server-side push is not configured. Please set VAPID keys in backend .env.
              </div>
            ) : pushPermission === 'denied' ? (
              <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-xs text-red-700">
                Notifications are blocked in your browser settings. Please allow notifications for this site and retry.
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {pushSubscribed ? (
                  <>
                    <Button type="button" onClick={disablePush} disabled={pushBusy} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                      {pushBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellOff className="w-4 h-4 mr-2" />}
                      Disable
                    </Button>
                    <Button type="button" onClick={testPush} disabled={pushBusy} variant="outline">
                      {pushBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <SendIcon className="w-4 h-4 mr-2" />}
                      Send Test
                    </Button>
                  </>
                ) : (
                  <Button type="button" onClick={enablePush} disabled={pushBusy} className="bg-[#9a1b2f] hover:bg-[#7a1524]">
                    {pushBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
                    Enable Push Notifications
                  </Button>
                )}
              </div>
            )}
            <div className="mt-3 grid grid-cols-3 gap-2 text-[10px]">
              {[
                { label: 'Service Requests', color: 'bg-amber-50 text-amber-700' },
                { label: 'Property Submissions', color: 'bg-purple-50 text-purple-700' },
                { label: 'Property Interests', color: 'bg-emerald-50 text-emerald-700' },
              ].map((c) => (
                <div key={c.label} className={`rounded-lg py-1.5 px-2 text-center font-semibold ${c.color}`}>{c.label}</div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-gray-900 text-sm uppercase tracking-wider flex items-center gap-2"><Camera className="w-4 h-4 text-[#9a1b2f]" /> Homepage Hero Slides</h3>
              <span className="text-[10px] uppercase font-semibold text-gray-400">{form.hero_slides.length}/{MAX_HERO_SLIDES}</span>
            </div>
            <p className="text-xs text-gray-500 mb-4">Upload up to {MAX_HERO_SLIDES} banner images for the homepage carousel. Wide 16:5 or 16:6 aspect ratio works best. When empty, the default banners are shown.</p>

            {form.hero_slides.length === 0 ? (
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-full aspect-[16/5] rounded-2xl border-2 border-dashed border-gray-200 hover:border-[#9a1b2f] hover:bg-[#fef2f4] transition-colors flex flex-col items-center justify-center text-gray-500 hover:text-[#9a1b2f]">
                {uploading ? <Loader2 className="w-6 h-6 animate-spin" /> : (
                  <>
                    <ImagePlus className="w-8 h-8" />
                    <span className="mt-2 text-sm font-semibold">Upload hero images</span>
                    <span className="text-[11px] text-gray-400">Currently using default banners</span>
                  </>
                )}
              </button>
            ) : (
              <div className="space-y-3">
                {form.hero_slides.map((img, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-xl border border-gray-100 bg-gray-50">
                    <div className="w-32 h-20 rounded-lg overflow-hidden bg-gray-100 shrink-0">
                      <img src={img} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-gray-900">Slide {i + 1}</div>
                      <div className="text-[11px] text-gray-500 mt-0.5">Position {i + 1} of {form.hero_slides.length}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => moveSlide(i, -1)} disabled={i === 0} className="w-8 h-8 rounded-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed text-gray-500 flex items-center justify-center" title="Move up">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moveSlide(i, 1)} disabled={i === form.hero_slides.length - 1} className="w-8 h-8 rounded-lg hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed text-gray-500 flex items-center justify-center" title="Move down">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeSlide(i)} className="w-8 h-8 rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-600 flex items-center justify-center" title="Remove">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  {form.hero_slides.length < MAX_HERO_SLIDES && (
                    <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="flex-1">
                      {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                      Add More Slides
                    </Button>
                  )}
                  <Button type="button" variant="outline" onClick={resetSlides} className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200">
                    Reset to Default
                  </Button>
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => handleAddSlides(e.target.files)}
            />
            <p className="text-[10px] text-gray-500 mt-2">JPG or PNG, up to 10MB each. Images are auto-resized and compressed.</p>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h3 className="font-bold text-gray-900 mb-4 text-sm uppercase tracking-wider">Other Contact Info</h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="cp" className="text-xs">Contact Phone</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input id="cp" value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} className="h-10 pl-9" placeholder="+91 98100 00000" />
                </div>
              </div>
              <div>
                <Label htmlFor="ce" className="text-xs">Contact Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <Input id="ce" type="email" value={form.contact_email} onChange={(e) => set('contact_email', e.target.value)} className="h-10 pl-9" placeholder="hello@safehouse.com" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <aside className="space-y-4 lg:sticky lg:top-24 h-fit">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider mb-3">Live Preview</h4>
            <p className="text-xs text-gray-500 mb-3">This is what visitors will click:</p>
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 h-11 rounded-full bg-[#25D366] hover:bg-[#20b955] text-white text-sm font-semibold transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
            <div className="mt-3 text-[11px] text-gray-500 break-all font-mono">{previewUrl}</div>
            <a href={previewUrl} target="_blank" rel="noopener noreferrer" className="mt-2 text-xs text-[#9a1b2f] hover:underline inline-flex items-center gap-1">
              Test link <ExternalLink className="w-3 h-3" />
            </a>
          </div>

          {form.hero_slides.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <h4 className="font-bold text-gray-900 text-sm uppercase tracking-wider mb-3">Hero Preview</h4>
              <div className="aspect-[16/5] rounded-xl overflow-hidden bg-gray-100">
                <img src={form.hero_slides[0]} alt="First slide" className="w-full h-full object-cover" />
              </div>
              <div className="text-[11px] text-gray-500 mt-2">{form.hero_slides.length} slide{form.hero_slides.length > 1 ? 's' : ''} will rotate on the homepage.</div>
            </div>
          )}

          <Button type="submit" disabled={saving} className="w-full h-11 bg-[#9a1b2f] hover:bg-[#7a1524]">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Settings
          </Button>
        </aside>
      </form>
    </AdminLayout>
  );
};

export default AdminSettingsPage;
